import { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSite } from '~/api/app/sites.api'
import { useStack } from '~/api/stacks.api'
import { Colors } from '~/assets/style/colors'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { Spinner } from '~/components/Spinner'
import { EditStackFlyout } from '~/modules/pbs/sites/site/stacks/EditStackFlyout'
import { ViewStackFlyout } from '~/modules/pbs/sites/site/stacks/ViewStackFlyout'

type Props = {
  stackId: string
  siteId: string
  scopeId: string
}

/**
 * Loads a deployment center row into `ViewStackFlyout`.
 *
 * The flyout wants a whole `Stack` and a whole `Site`; a row here carries neither, on purpose — the list
 * projection leaves out `configuration`, `snapshot` and `output` so that a page of a hundred groups does
 * not ship every ad unit of every One stack just to draw a table. The two documents are fetched here, for
 * the one stack actually opened, which is the trade the projection was making.
 *
 * Both hooks are cached by react-query, so reopening the same row costs nothing.
 */
export const DeploymentCenterStackFlyout: FunctionComponent<Props> = ({ stackId, siteId, scopeId }) => {
  const { data: stack, isLoading: isLoadingStack } = useStack(stackId)
  const { data: site, isLoading: isLoadingSite } = useSite(siteId, scopeId)
  const flyout = useGlobalFlyout()
  const navigate = useNavigate()

  if (isLoadingStack || isLoadingSite || !stack || !site) {
    return <Spinner />
  }

  /*
   * The same way out of reading and into editing the media's own stack list offers, so a stack opened
   * from the deployment center is not a dead end that has to be found a second time under its media.
   *
   * The two products edit in different places, and neither is a detail this flyout can paper over: a Node
   * stack is a flyout's worth of settings and opens as one, while a One stack is a page of ad units and
   * rules. `ViewStackFlyout` still decides whether to offer the button at all — only a `to-deploy` stack
   * can be edited in place, since a live one is changed by creating the next version.
   */
  const onEditStack = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (stack.type === 'pbs') {
      flyout.open(EditStackFlyout, { color: Colors.King, closeTrackingLabel: 'pbs-sites-stacks-edit-close' }, { mode: 'edit', siteId: site.id, stack, onClose: () => flyout.close() })
    } else {
      navigate(`/monetization/one/sites/${stack.siteId}/stacks/${stack.id}`)
    }
  }

  return <ViewStackFlyout site={site} stack={stack} onEditStack={onEditStack} />
}
